import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchPrices,
  deletePrice,
  updatePrice,
  createPrice,
} from "../../../redux/admin/adminOperations";
import { Price } from "../../../redux/types/types";
import Notiflix from "notiflix";
import { useIntl } from "react-intl";

type Order = "asc" | "desc";

export const usePricesManagement = () => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const {
    prices = [],
    loading,
    error,
  } = useSelector((state: RootState) => state.admin);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [priceToDelete, setPriceToDelete] = useState<number | null>(null);
  const [priceToEdit, setPriceToEdit] = useState<Price | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof Price>("createdAt");
  const [order, setOrder] = useState<Order>("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const fetchPriceData = useCallback(
    (searchTerm?: string) => {
      dispatch(fetchPrices(searchTerm));
    },
    [dispatch]
  );

  const handleDeletePrice = useCallback((priceId: number) => {
    setPriceToDelete(priceId);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeletePrice = useCallback(() => {
    if (priceToDelete) {
      dispatch(deletePrice(priceToDelete))
        .unwrap()
        .then(() => {
          fetchPriceData(searchTerm);
          setDeleteDialogOpen(false);
          setPriceToDelete(null);
          setDeleteError(null);
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.dashboard.deletePriceSuccess" })
          );
        })
        .catch((error) => {
          let errorKey = "admin.dashboard.deletePriceError";
          if (error.message === "Price not found.") {
            errorKey = "admin.dashboard.priceNotFound";
          } else if (
            error.message === "Cannot delete price because it is in use."
          ) {
            errorKey = "admin.dashboard.priceInUse";
          }
          setDeleteError(errorKey);
        });
    }
  }, [priceToDelete, dispatch, fetchPriceData, intl, searchTerm]);

  const handleEditPrice = useCallback((price: Price) => {
    setPriceToEdit(price);
    setEditDialogOpen(true);
  }, []);

  const handleUpdatePrice = useCallback(
    (price: Price) => {
      if (price && price.id) {
        dispatch(
          updatePrice({
            id: price.id,
            data: {
              numberOfClasses: price.numberOfClasses,
              specialType: price.specialType,
              amount: price.amount,
              currency: price.currency,
              classId: price.classId,
              isAdminOnly: price.isAdminOnly,
            },
          })
        )
          .unwrap()
          .then(() => {
            fetchPriceData(searchTerm);
            setEditDialogOpen(false);
            setPriceToEdit(null);
            Notiflix.Notify.success(
              intl.formatMessage({ id: "admin.dashboard.updatePriceSuccess" })
            );
          })
          .catch((error) => {
            Notiflix.Notify.failure(
              intl.formatMessage({ id: "admin.dashboard.updatePriceError" })
            );
            console.error("Failed to update price:", error);
          });
      }
    },
    [dispatch, fetchPriceData, intl, searchTerm]
  );

  const onPriceUpdate = async (price: Price) => {
    await setPriceToEdit(price);
    await handleUpdatePrice(price);
  };

  const handleCreatePrice = useCallback(
    (priceData: {
      specialType?: string | null;
      numberOfClasses: number;
      amount: number;
      currency: string;
      classId?: number | null;
      isAdminOnly?: boolean;
    }) => {
      dispatch(createPrice(priceData))
        .unwrap()
        .then(() => {
          fetchPriceData(searchTerm);
          setCreateDialogOpen(false);
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.dashboard.createPriceSuccess" })
          );
        })
        .catch((error) => {
          Notiflix.Notify.failure(
            intl.formatMessage({ id: "admin.dashboard.createPriceError" })
          );
          console.error("Failed to create price:", error);
        });
    },
    [dispatch, fetchPriceData, intl, searchTerm]
  );

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const handleRequestSort = useCallback(
    (property: keyof Price) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value;
      setSearchTerm(newSearchTerm);
      setPage(0);

      // Debounce the search to avoid too many API calls
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(() => {
        fetchPriceData(newSearchTerm);
      }, 300); // 300ms debounce

      setSearchTimeout(timeout);
    },
    [fetchPriceData, searchTimeout]
  );

  // Since we're doing search on the backend, we can simplify the filtering here
  // The backend already handles the price search logic for both cents and decimal formats
  const filteredPrices = prices || [];

  const sortedPrices = [...filteredPrices].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    // Handle undefined/null values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return order === "asc" ? -1 : 1;
    if (bValue == null) return order === "asc" ? 1 : -1;

    // Handle date values (createdAt, updatedAt)
    if (orderBy === "createdAt" || orderBy === "updatedAt") {
      const aDate = new Date(aValue as string | number | Date).getTime();
      const bDate = new Date(bValue as string | number | Date).getTime();
      if (aDate < bDate) {
        return order === "asc" ? -1 : 1;
      }
      if (aDate > bDate) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    }

    // Handle other values
    if (aValue < bValue) {
      return order === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });

  const paginatedPrices = sortedPrices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPrices = filteredPrices.length;

  return {
    prices: paginatedPrices,
    totalPrices,
    loading,
    error,
    deleteDialogOpen,
    editDialogOpen,
    createDialogOpen,
    priceToDelete,
    priceToEdit,
    page,
    rowsPerPage,
    orderBy,
    order,
    searchTerm,
    deleteError,
    fetchPriceData,
    handleDeletePrice,
    confirmDeletePrice,
    handleEditPrice,
    handleUpdatePrice,
    handleCreatePrice,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRequestSort,
    handleSearchChange,
    setDeleteDialogOpen,
    setEditDialogOpen,
    setCreateDialogOpen,
    setPriceToEdit,
    setDeleteError,
    onPriceUpdate,
  };
};
